import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import * as api from '../services/api';
import { getUserProfile } from '../services/authService';
import './Dashboard.css';
import { useNavigate } from 'react-router-dom';

const Dashboard = () => {
  const [activeTab, setActiveTab] = useState('vault');
  const [vaultFiles, setVaultFiles] = useState([]);
  const [publicFiles, setPublicFiles] = useState([]);
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState({
    vault: false,
    public: false,
    forms: false
  });
  const [fileUploading, setFileUploading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [selectedFolder, setSelectedFolder] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [userProfile, setUserProfile] = useState(null);
  
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  
  // Check if user is admin
  const isAdmin = currentUser && currentUser.role === 'admin';
  
  // Effect for redirect if not authenticated
  useEffect(() => {
    if (!currentUser) {
      navigate('/signin?redirect=/dashboard');
    }
  }, [currentUser, navigate]);
  
  // Fetch user profile information
  useEffect(() => {
    if (currentUser) {
      const fetchUserProfile = async () => {
        try {
          const profileData = await getUserProfile();
          setUserProfile(profileData);
        } catch (error) {
          console.error('Error fetching user profile:', error);
        }
      };
      
      fetchUserProfile();
    }
  }, [currentUser]);
  
  // Separate effect for fetching files based on active tab
  useEffect(() => {
    // Don't try to fetch if not authenticated
    if (!currentUser) return;
    
    const fetchFiles = async () => {
      try {
        if (activeTab === 'vault') {
          setLoading(prev => ({ ...prev, vault: true }));
          const response = await api.getVaultFiles();
          // Filter out empty directory markers
          const filteredFiles = (response.files || []).filter(file => 
            file.file_path && file.file_path.split('/').pop()
          );
          setVaultFiles(filteredFiles);
          setLoading(prev => ({ ...prev, vault: false }));
        } else if (activeTab === 'public') {
          setLoading(prev => ({ ...prev, public: true }));
          const response = await api.getPublicFiles();
          // Filter out empty directory markers
          const filteredFiles = (response.files || []).filter(file => 
            file.name && file.name.trim() !== ''
          );
          setPublicFiles(filteredFiles);
          setLoading(prev => ({ ...prev, public: false }));
        } else if (activeTab === 'forms') {
          setLoading(prev => ({ ...prev, forms: true }));
          const response = await api.getFormsLibrary();
          // Filter out empty directory markers
          const filteredForms = (response.forms || []).filter(form => 
            form.name && form.name.trim() !== ''
          );
          setForms(filteredForms);
          setLoading(prev => ({ ...prev, forms: false }));
        }
      } catch (error) {
        console.error(`Error fetching ${activeTab} files:`, error);
        setError(`Failed to load ${activeTab} files. Please try again.`);
        setLoading({
          vault: false,
          public: false,
          forms: false
        });
      }
    };
    
    fetchFiles();
  }, [activeTab, currentUser]);
  
  const handleFileUpload = async (event, type) => {
    const file = event.target.files[0];
    if (!file) return;
    
    // Check file size (200MB limit)
    const MAX_FILE_SIZE = 200 * 1024 * 1024; // 200MB in bytes
    if (file.size > MAX_FILE_SIZE) {
      setError(`File size exceeds the 200MB limit. Please upload a smaller file.`);
      // Clear the file input
      event.target.value = null;
      return;
    }
    
    setFileUploading(true);
    setError(null);
    setSuccessMessage(null);
    
    try {
      if (type === 'vault') {
        await api.uploadVaultFile(file);
        // Refresh vault files
        const response = await api.getVaultFiles();
        const filteredFiles = (response.files || []).filter(file => 
          file.file_path && file.file_path.split('/').pop()
        );
        setVaultFiles(filteredFiles);
        setSuccessMessage(`File '${file.name}' uploaded successfully to your vault.`);
      } else if (type === 'public' && isAdmin) {
        await api.uploadPublicFile(file, selectedFolder);
        // Refresh public files
        const response = await api.getPublicFiles();
        const filteredFiles = (response.files || []).filter(file => 
          file.name && file.name.trim() !== ''
        );
        setPublicFiles(filteredFiles);
        setSuccessMessage(`File '${file.name}' uploaded successfully to public files.`);
      } else if (type === 'forms' && isAdmin) {
        await api.uploadForm(file, selectedFolder);
        // Refresh forms
        const response = await api.getFormsLibrary();
        const filteredForms = (response.forms || []).filter(form => 
          form.name && form.name.trim() !== ''
        );
        setForms(filteredForms);
        setSuccessMessage(`Form '${file.name}' uploaded successfully.`);
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      setError('Failed to upload file. Please try again.');
    } finally {
      setFileUploading(false);
      // Clear the file input
      event.target.value = null;
    }
  };
  
  const handleFileDownload = async (filename, type) => {
    setError(null);
    
    try {
      let response;
      if (type === 'vault') {
        response = await api.downloadVaultFile(filename);
      } else if (type === 'public') {
        response = await api.downloadPublicFile(filename);
      } else if (type === 'forms') {
        response = await api.downloadForm(filename);
      }
      
      // Create a download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Extract actual filename from path if needed
      const actualFilename = filename && filename.includes('/') ? filename.split('/').pop() : filename;
      link.setAttribute('download', actualFilename || 'download');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading file:', error);
      setError('Failed to download file. Please try again.');
    }
  };
  
  const handleFileDelete = async (filename, type) => {
    if (!window.confirm(`Are you sure you want to delete "${filename}"?`)) {
      return;
    }
    
    setError(null);
    setSuccessMessage(null);
    
    try {
      if (type === 'vault') {
        await api.deleteVaultFile(filename);
        // Refresh vault files
        const response = await api.getVaultFiles();
        const filteredFiles = (response.files || []).filter(file => 
          file.file_path && file.file_path.split('/').pop()
        );
        setVaultFiles(filteredFiles);
        setSuccessMessage(`File '${filename}' deleted successfully.`);
      } else if (type === 'public' && isAdmin) {
        await api.deletePublicFile(filename);
        // Refresh public files
        const response = await api.getPublicFiles();
        const filteredFiles = (response.files || []).filter(file => 
          file.name && file.name.trim() !== ''
        );
        setPublicFiles(filteredFiles);
        setSuccessMessage(`File '${filename}' deleted successfully.`);
      } else if (type === 'forms' && isAdmin) {
        await api.deleteForm(filename);
        // Refresh forms
        const response = await api.getFormsLibrary();
        const filteredForms = (response.forms || []).filter(form => 
          form.name && form.name.trim() !== ''
        );
        setForms(filteredForms);
        setSuccessMessage(`Form '${filename}' deleted successfully.`);
      }
    } catch (error) {
      console.error('Error deleting file:', error);
      setError('Failed to delete file. Please try again.');
    }
  };
  
  // Filter files based on search term
  const getFilteredFiles = (files, type) => {
    if (!searchTerm) return files;
    
    return files.filter(file => {
      const fileName = type === 'vault' 
        ? (file.file_path ? file.file_path.split('/').pop() : '') 
        : (file.name || '');
      
      return fileName.toLowerCase().includes(searchTerm.toLowerCase());
    });
  };
  
  const renderFilesList = (files, type) => {
    // Apply search filter
    const filteredFiles = getFilteredFiles(files, type);
    
    return (
      <div className="files-list">
        {filteredFiles.length === 0 ? (
          <p>No files found.</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>File Name</th>
                <th>Size</th>
                {type === 'vault' && <th>Uploaded At</th>}
                {type !== 'vault' && <th>Last Modified</th>}
                <th>Uploaded By</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredFiles.map((file, index) => {
                // Safely determine the filename to display and use for actions
                const fileName = file.name || (file.file_path ? file.file_path.split('/').pop() : 'Unknown file');
                
                // Determine who uploaded the file based on filename or path
                const isRkubeFile = fileName.startsWith('rk-');
                let uploader;
                
                if (isRkubeFile) {
                  // Use rkube svg for rkube files
                  uploader = (
                    <div className="rkube-uploader">
                      <svg width="25" height="20" viewBox="0 0 282 104" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <defs>
                          <linearGradient id={`paint${index}`} x1="0" y1="0" x2="282.286" y2="104" gradientUnits="userSpaceOnUse">
                            <stop stopColor="#8F05FC"></stop>
                            <stop offset="1" stopColor="#5024FF"></stop>
                          </linearGradient>
                        </defs>
                        <text x="10" y="70" fontSize="90" fontWeight="bold" fill={`url(#paint${index})`} fontFamily="Arial, sans-serif">rkube</text>
                      </svg>
                    </div>
                  );
                } else if (userProfile) {
                  // Get user initials from profile data
                  const firstInitial = userProfile.first_name ? userProfile.first_name.charAt(0).toUpperCase() : '';
                  const lastInitial = userProfile.last_name ? userProfile.last_name.charAt(0).toUpperCase() : '';
                  const initials = firstInitial + lastInitial;
                  
                  uploader = (
                    <div className="user-initials">
                      {initials || userProfile.username?.substring(0, 2).toUpperCase() || 'U'}
                    </div>
                  );
                } else {
                  // Fallback to currentUser if profile not loaded yet
                  const initials = `${currentUser?.first_name?.charAt(0) || ''}${currentUser?.last_name?.charAt(0) || ''}`;
                  uploader = (
                    <div className="user-initials">
                      {initials || currentUser?.username?.substring(0, 2) || 'U'}
                    </div>
                  );
                }
                
                return (
                  <tr key={index}>
                    <td>{fileName}</td>
                    <td>{file.size ? formatFileSize(file.size) : 'N/A'}</td>
                    <td>
                      {file.uploaded_at 
                        ? new Date(file.uploaded_at).toLocaleDateString() 
                        : file.last_modified 
                          ? new Date(file.last_modified).toLocaleDateString()
                          : 'N/A'}
                    </td>
                    <td className="uploader-cell">{uploader}</td>
                    <td>
                      <button 
                        className="action-btn download" 
                        onClick={() => handleFileDownload(fileName, type)}
                      >
                        Download
                      </button>
                      {(type === 'vault' || isAdmin) && (
                        <button 
                          className="action-btn delete" 
                          onClick={() => handleFileDelete(fileName, type)}
                        >
                          Delete
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    );
  };
  
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };
  
  // Check if we have any search results to display
  const hasVaultSearchResults = searchTerm && getFilteredFiles(vaultFiles, 'vault').length > 0;
  const hasFormsSearchResults = searchTerm && getFilteredFiles(forms, 'forms').length > 0;
  const hasPublicSearchResults = searchTerm && getFilteredFiles(publicFiles, 'public').length > 0;
  
  return (
    <div className="dashboard-container">
      <h1>Dashboard</h1>
      
      {/* Add search field */}
      <div className="search-container">
        <div className="search-input-wrapper">
          <input
            type="text"
            placeholder="Search for files across all categories..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          {searchTerm && (
            <button 
              className="search-clear-btn" 
              onClick={() => setSearchTerm('')}
              aria-label="Clear search"
            >
              ×
            </button>
          )}
        </div>
      </div>
      
      {/* If not searching, show the tabs */}
      {!searchTerm && (
        <div className="tabs">
          <button 
            className={`tab ${activeTab === 'vault' ? 'active' : ''}`}
            onClick={() => setActiveTab('vault')}
          >
            My Vault
          </button>
          <button 
            className={`tab ${activeTab === 'forms' ? 'active' : ''}`}
            onClick={() => setActiveTab('forms')}
          >
            Forms Library
          </button>
          <button 
            className={`tab ${activeTab === 'public' ? 'active' : ''}`}
            onClick={() => setActiveTab('public')}
          >
            Public Files
          </button>
        </div>
      )}
      
      {error && <div className="error-message">{error}</div>}
      {successMessage && <div className="success-message">{successMessage}</div>}
      
      <div className="tab-content">
        {/* When searching, always show all sections with results */}
        {(searchTerm || activeTab === 'vault') && (
          <div className={`tab-pane ${searchTerm ? 'search-results' : ''}`}>
            <h2>My Private Files</h2>
            {!searchTerm && (
              <>
                <p>Upload, download, and manage your private files. These files are encrypted and only accessible to you.</p>
                
                <div className="file-actions">
                  <div className="upload-container">
                    <label className="upload-btn">
                      {fileUploading ? 'Uploading...' : 'Upload File'}
                      <input 
                        type="file" 
                        onChange={(e) => handleFileUpload(e, 'vault')} 
                        disabled={fileUploading} 
                      />
                    </label>
                    <small className="file-size-limit">Maximum file size: 200MB. For multiple files, consider uploading a zip file.</small>
                  </div>
                </div>
              </>
            )}
            
            {loading.vault ? (
              <p>Loading vault files...</p>
            ) : (
              renderFilesList(vaultFiles, 'vault')
            )}
          </div>
        )}
        
        {(searchTerm || activeTab === 'forms') && (
          <div className={`tab-pane ${searchTerm ? 'search-results' : ''}`}>
            <h2>Forms Library</h2>
            {!searchTerm && (
              <>
                <p>Access tax, accounting, and compliance forms provided by our team.</p>
                
                {isAdmin && (
                  <div className="file-actions">
                    <div className="folder-select">
                      <input 
                        type="text" 
                        placeholder="Folder path (optional)" 
                        value={selectedFolder} 
                        onChange={(e) => setSelectedFolder(e.target.value)} 
                      />
                    </div>
                    <div className="upload-container">
                      <label className="upload-btn">
                        {fileUploading ? 'Uploading...' : 'Upload Form'}
                        <input 
                          type="file" 
                          onChange={(e) => handleFileUpload(e, 'forms')} 
                          disabled={fileUploading} 
                        />
                      </label>
                      <small className="file-size-limit">Maximum file size: 200MB. For multiple files, consider uploading a zip file.</small>
                    </div>
                  </div>
                )}
              </>
            )}
            
            {loading.forms ? (
              <p>Loading forms...</p>
            ) : (
              renderFilesList(forms, 'forms')
            )}
          </div>
        )}
        
        {(searchTerm || activeTab === 'public') && (
          <div className={`tab-pane ${searchTerm ? 'search-results' : ''}`}>
            <h2>Public Files</h2>
            {!searchTerm && (
              <>
                <p>Access publicly available documents shared by our organization.</p>
                
                {isAdmin && (
                  <div className="file-actions">
                    <div className="folder-select">
                      <input 
                        type="text" 
                        placeholder="Folder path (optional)" 
                        value={selectedFolder} 
                        onChange={(e) => setSelectedFolder(e.target.value)} 
                      />
                    </div>
                    <div className="upload-container">
                      <label className="upload-btn">
                        {fileUploading ? 'Uploading...' : 'Upload File'}
                        <input 
                          type="file" 
                          onChange={(e) => handleFileUpload(e, 'public')} 
                          disabled={fileUploading} 
                        />
                      </label>
                      <small className="file-size-limit">Maximum file size: 200MB. For multiple files, consider uploading a zip file.</small>
                    </div>
                  </div>
                )}
              </>
            )}
            
            {loading.public ? (
              <p>Loading public files...</p>
            ) : (
              renderFilesList(publicFiles, 'public')
            )}
          </div>
        )}
        
        {/* Show message when searching with no results */}
        {searchTerm && !hasVaultSearchResults && !hasFormsSearchResults && !hasPublicSearchResults && (
          <div className="no-search-results">
            <p>No files found matching "{searchTerm}"</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard; 